import { StorageError } from "../../mobx/lib/error";
import { JSONTransformer } from "../transformer";
import { emptyStorageAdapter } from "./empty";
import type { StorageErrorKind } from "../../mobx/lib/error";
import type { StorageAdapter } from "../adapter";
import type { DataTransformer } from "../transformer";

export const localStorageAdapter = <DT extends DataTransformer>(
  transformer: DT = JSONTransformer as DT,
): StorageAdapter<DT> => {
  const _error = (kind: StorageErrorKind, message: string) =>
    new StorageError("localStorage", kind, message);

  if (typeof window === "undefined") {
    return emptyStorageAdapter(transformer);
  }

  const knownIndices: Record<
    string,
    {
      stale: boolean;
      idx: Record<string, number>;
    }
  > = {};

  return {
    transformer,
    type: "localStorage" as const,

    async getString(model: string, key: string) {
      return Promise.resolve(window.localStorage.getItem(`${model}/${key}`));
    },
    async get<T>(model: string, key: string) {
      const item = await this.getString(model, key);
      if (!item) {
        return null;
      }
      try {
        return this.transformer.deserialize<T>(item);
      } catch (e) {
        window.console.group("Error parsing localStorage item for key:", key);
        window.console.error(e);
        window.console.log(key);
        window.console.log("Returned stringified item:");
        window.console.log(item);
        window.console.groupEnd();
        return null;
      }
    },
    async getModels<T>(model: string) {
      console.log(
        "localStorageAdapter.getModels: Fetching models for [%s]",
        model,
      );
      if (!knownIndices[model]) {
        knownIndices[model] = {
          stale: true,
          idx: {},
        };
      }

      if (knownIndices[model].stale) {
        // very scary very very blocking loop
        // seriously don't use this adapter if you have a lot of data
        for (let i = 0, finished = false; !finished; i++) {
          const key = window.localStorage.key(i);
          if (key) {
            const [modelName, modelId] = key.split("/");

            if (modelName === model) {
              knownIndices[model].idx[modelId] = i;
            }
          } else {
            finished = true;
          }
          if (i === 1500) {
            window.console.warn(
              "localStorageAdapter.getModels: Exceeded 1500 iterations while reindexing model [%s], THIS IS LIKELY EXTREMELY EXPENSIVE. You may run into `RangeError` issues.",
              model,
            );
          }
        }
      }

      const models = await Promise.all(
        Object.entries(knownIndices[model].idx).map(([modelId]) =>
          this.get<T>(model, modelId),
        ),
      );

      return models.filter((m) => m !== null) as T[];
    },
    async setString(model: string, key: string, value: string) {
      try {
        if (!knownIndices[model]) {
          knownIndices[model] = {
            stale: true,
            idx: {},
          };
        }

        if (!knownIndices[model].idx[key]) {
          knownIndices[model].stale = true;
        }

        window.localStorage.setItem(`${model}/${key}`, value);
        return Promise.resolve(true);
      } catch (e) {
        // quota exceeded error most likely (only?)
        window.console.error(e);
        return Promise.resolve(false);
      }
    },
    async set<T>(model: string, key: string, value: T) {
      try {
        const stringifiedValue = this.transformer.serialize(value);
        await this.setString(model, key, stringifiedValue);
        return true;
      } catch (e) {
        window.console.error(e);
        return false;
      }
    },
    async setModels(model, values) {
      return false;
    },
    // eslint-disable-next-line @typescript-eslint/require-await
    async delete(model: string, key: string) {
      window.localStorage.removeItem(`${model}/${key}`);
      return true;
    },
    async reset(model) {
      return false;
    },
  };
};
